import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Modal, Button, Form, Dropdown } from 'react-bootstrap';
import apiClient from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { roleTranslations } from '../utils/translations';
import { toast } from 'react-toastify';
import ConfirmationModal from '../components/ConfirmationModal';
import AssignRolesModal from '../components/AssignRolesModal'; // MODIFIED
import UnavailabilityTooltip from '../components/UnavailabilityTooltip';

// Custom toggle for the actions dropdown
const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
    <button
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
      className="btn btn-link text-dark p-3 border-0"
    >
      {children}
    </button>
));

const OrganizationUsersPage = () => {
    const { orgId } = useParams();
    const { user: currentUser } = useAuth();
    const { canCreate, canUpdate, canDelete, loading: permissionsLoading } = usePermissions('userManagement');

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteMessage, setInviteMessage] = useState('');
    const [inviteError, setInviteError] = useState('');

    // State for modals
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [showEditRoleModal, setShowEditRoleModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [selectedRole, setSelectedRole] = useState('');
    const [showAssignRolesModal, setShowAssignRolesModal] = useState(false); // MODIFIED
    const [userToEditRoles, setUserToEditRoles] = useState(null); // MODIFIED

    // State for ownership transfer confirmation modal
    const [showTransferOwnershipModal, setShowTransferOwnershipModal] = useState(false);


    const handleApiError = (err, defaultMessage) => {
        if (err.response && err.response.data && err.response.data.detail) {
            if (Array.isArray(err.response.data.detail)) {
                return err.response.data.detail.map(e => `${e.loc.join(' -> ')}: ${e.msg}`).join('; ');
            } else {
                return err.response.data.detail;
            }
        } else {
            return defaultMessage;
        }
    };

    const refreshUsers = useCallback(() => {
        apiClient.get(`/users/`)
            .then(response => {
                setUsers(response.data);
            })
            .catch(err => {
                toast.error('Не удалось обновить список пользователей.');
            });
    }, []);

    useEffect(() => {
        setLoading(true);
        apiClient.get(`/users/`)
            .then(response => {
                setUsers(response.data);
            })
            .catch(err => {
                setError('Ошибка загрузки списка пользователей');
            })
            .finally(() => {
                setLoading(false);
            });
    }, [orgId]);

    const handleInvite = async (e) => {
        e.preventDefault();
        setInviteMessage('');
        setInviteError('');
        try {
            const response = await apiClient.post('/organizations/invite', { email: inviteEmail });
            setInviteMessage(response.data.message);
            if (response.data.invitation_link) {
                setInviteMessage(prev => `${prev} Ссылка для приглашения: ${response.data.invitation_link}`);
            }
            setInviteEmail('');
        } catch (err) {
            setInviteError(handleApiError(err, 'Ошибка создания приглашения'));
        }
    };

    const handleSaveRole = async () => {
        if (!editingUser) return;

        if (selectedRole === 'owner') {
            if (editingUser.id === currentUser.id) {
                toast.error('Вы не можете передать владение самому себе.');
                return;
            }
            setShowEditRoleModal(false); // Close edit modal
            setShowTransferOwnershipModal(true); // Open confirmation modal
        } else {
            try {
                await apiClient.put(`/users/${editingUser.id}/role`, { role: selectedRole });
                refreshUsers(); // Refresh without page jump
                toast.success('Права доступа успешно изменены!');
                handleCloseEditModal();
            } catch (err) {
                toast.error(handleApiError(err, 'Произошла ошибка при изменении прав доступа'));
            }
        }
    };

    const handleConfirmTransferOwnership = async () => {
        try {
            await apiClient.put(
                `/users/${editingUser.id}/transfer-ownership`,
                { new_owner_user_id: editingUser.id }
            );
            toast.success('Владение организацией успешно передано!');
            refreshUsers();
            handleCloseEditModal();
            setShowTransferOwnershipModal(false);
        } catch (err) {
            toast.error(handleApiError(err, 'Произошла ошибка при передаче владения'));
        }
    };

    const handleEditRoleClick = (user) => {
        setEditingUser(user);
        setSelectedRole(user.role_in_organization);
        setShowEditRoleModal(true);
    };

    const handleCloseEditModal = () => {
        setShowEditRoleModal(false);
        setEditingUser(null);
        setSelectedRole('');
    };

    const handleEditRolesClick = (user) => { // MODIFIED
        setUserToEditRoles(user); // MODIFIED
        setShowAssignRolesModal(true); // MODIFIED
    };

    const roleHierarchy = {
        owner: 3,
        admin: 2,
        editor: 1,
        member: 0
    };

    const canChangeRole = (targetUser) => {
        if (!currentUser || !targetUser) return false;
        if (currentUser.id === targetUser.id) return false;
        if (currentUser.is_superuser) return true;

        const currentUserLevel = roleHierarchy[currentUser.role_in_organization];
        const targetUserLevel = roleHierarchy[targetUser.role_in_organization];

        if (currentUserLevel === undefined || targetUserLevel === undefined) return false;

        // Можно изменять роль только тех, кто ниже по иерархии
        return currentUserLevel > targetUserLevel;
    };

    const getAssignableRoles = () => {
        if (!currentUser) return [];
    
        const allRoles = [
            { value: 'member', label: roleTranslations.member },
            { value: 'editor', label: roleTranslations.editor },
            { value: 'admin', label: roleTranslations.admin },
        ];
    
        if (currentUser.is_superuser) {
            return [...allRoles, { value: 'owner', label: roleTranslations.owner }];
        }
    
        const currentUserLevel = roleHierarchy[currentUser.role_in_organization];
        if (currentUserLevel === undefined) return [];
    
        // Можно назначать только роли, которые ниже собственной
        return allRoles.filter(role => roleHierarchy[role.value] < currentUserLevel);
    };

    const handleDeleteClick = (user) => {
        setUserToDelete(user);
        setShowConfirmDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (userToDelete) {
            try {
                await apiClient.delete(`/users/${userToDelete.id}/organization`);
                refreshUsers();
                toast.success('Пользователь успешно удален из организации!');
            } catch (err) {
                toast.error(handleApiError(err, 'Ошибка удаления пользователя'));
            } finally {
                setShowConfirmDeleteModal(false);
                setUserToDelete(null);
            }
        }
    };

    if (loading || permissionsLoading) return <div className="text-center">Загрузка...</div>;
    if (error && !users.length) return <div className="alert alert-danger">{error}</div>;

    const roleOrder = {
        'owner': 0,
        'admin': 1,
        'editor': 2,
        'member': 3
    };

    const sortedUsers = [...users].sort((a, b) => {
        const roleA = roleOrder[a.role_in_organization] ?? 99;
        const roleB = roleOrder[b.role_in_organization] ?? 99;

        if (roleA !== roleB) {
            return roleA - roleB;
        }

        const nameA = a.name || a.email;
        const nameB = b.name || b.email;
        return nameA.localeCompare(nameB);
    });

    const assignableRoles = getAssignableRoles();

    return (
        <>
            <h1 className="h2">Пользователи</h1>

            {canCreate && (
                <div className="card mb-4">
                    <div className="card-header">Пригласить нового пользователя</div>
                    <div className="card-body">
                        <form onSubmit={handleInvite}>
                            <div className="input-group">
                                <input type="email" className="form-control" placeholder="Email нового пользователя" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} required />
                                <button type="submit" className="btn btn-primary">Отправить приглашение</button>
                            </div>
                        </form>
                        {inviteMessage && (
                            <div className="alert alert-info mt-3">
                                {inviteMessage}
                            </div>
                        )}
                        {inviteError && (
                            <div className="alert alert-danger mt-3">
                                {inviteError}
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className="card mb-4">
                <div className="card-header">Список пользователей</div>
                <ul className="list-group list-group-flush">
                                        {sortedUsers.map(user => (
                        <li key={user.id} className="list-group-item d-flex justify-content-between align-items-center">
                            <div className="d-flex flex-column">
                                <div className="d-flex align-items-center">
                                    <span className="fw-bold">{user.name || 'Имя не указано'}</span>
                                    <UnavailabilityTooltip periods={user.unavailability_periods} />
                                </div>
                                <small className="text-muted">{user.email}</small>
                                <div className="mt-1">
                                    {user.roles?.map(role => ( // MODIFIED
                                        <span key={role.id} className="badge bg-primary me-1">{role.name}</span> // MODIFIED
                                    ))}
                                </div>
                            </div>
                            <div className="d-flex align-items-center">
                                <span className="badge bg-secondary me-2">{roleTranslations[user.role_in_organization] || user.role_in_organization}</span>
                                {canUpdate ? (
                                    <Dropdown>
                                        <Dropdown.Toggle as={CustomToggle} id={`dropdown-actions-${user.id}`}>
                                            <i className="fas fa-ellipsis-v"></i>
                                        </Dropdown.Toggle>
                                
                                        <Dropdown.Menu>
                                            <Dropdown.Item onClick={() => handleEditRolesClick(user)}>Изменить роли</Dropdown.Item>
                                            <Dropdown.Item onClick={() => handleEditRoleClick(user)} disabled={!canChangeRole(user)}>
                                                Изменить права доступа
                                            </Dropdown.Item>
                                            {canDelete && user.role_in_organization !== 'owner' && user.id !== currentUser.id &&
                                                <Dropdown.Item onClick={() => handleDeleteClick(user)} className="text-danger">Удалить</Dropdown.Item>}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                ) : (
                                    <div className="p-2" style={{ visibility: 'hidden' }}>
                                        <i className="fas fa-ellipsis-v"></i>
                                    </div>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Edit Role Modal */}
            <Modal show={showEditRoleModal} onHide={handleCloseEditModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Изменить права доступа:<br/>{editingUser?.name || editingUser?.email}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group>
                            <Form.Label>Выберите набор прав доступа</Form.Label>
                            <Form.Select value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)} autoFocus>
                                {assignableRoles.map(role => (
                                    <option key={role.value} value={role.value}>{role.label}</option>
                                ))}
                                {/* Allow selecting owner role only for transfer, which is handled separately */}
                                {(currentUser.role_in_organization === 'owner' || currentUser.is_superuser) && 
                                 !assignableRoles.find(r => r.value === 'owner') &&
                                 <option value="owner">{roleTranslations.owner}</option>
                                }
                            </Form.Select>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseEditModal}>Отмена</Button>
                    <Button variant="primary" onClick={handleSaveRole}>Сохранить</Button>
                </Modal.Footer>
            </Modal>

            {/* Assign Roles Modal */}
            {userToEditRoles && ( // MODIFIED
                <AssignRolesModal // MODIFIED
                    show={showAssignRolesModal} // MODIFIED
                    handleClose={() => setShowAssignRolesModal(false)} // MODIFIED
                    userToEdit={userToEditRoles} // MODIFIED
                    onUpdate={() => {
                        refreshUsers();
                        setShowAssignRolesModal(false); // MODIFIED
                    }}
                />
            )}

            {/* Ownership Transfer Confirmation Modal */}
            <ConfirmationModal
                show={showTransferOwnershipModal}
                title="Подтверждение передачи владения"
                message={`Вы уверены, что хотите передать владение организацией пользователю ${editingUser?.name || editingUser?.email}? Вы потеряете роль Владельца и станете Администратором.`}
                onConfirm={handleConfirmTransferOwnership}
                onCancel={() => setShowTransferOwnershipModal(false)}
                confirmText="Передать владение"
                cancelText="Отмена"
            />

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                show={showConfirmDeleteModal}
                title="Подтвердить удаление"
                message={`Вы уверены, что хотите удалить пользователя "${userToDelete?.name || userToDelete?.email}" из организации?`}
                onConfirm={handleConfirmDelete}
                onCancel={() => setShowConfirmDeleteModal(false)}
                confirmText="Удалить"
                cancelText="Отмена"
            />
        </>
    );
};

export default OrganizationUsersPage;